<template>
  <div>
    <span class="d-flex align-items-center">
      <h5
        role="button"
        @click="isOpen = !isOpen"
        class="user-select-none mb-0"
        :aria-expanded="isOpen ? 'true' : 'false'"
        aria-controls="collapse-zimmerkontingente-plan"
      >
        <b-icon v-if="!isOpen" :scale="0.75" icon="chevron-right" aria-hidden="true"></b-icon>
        <b-icon v-else :scale="0.75" icon="chevron-down" aria-hidden="true"></b-icon>
        Zimmerkontingente Plan
      </h5>
      <b-button
        variant="outline-primary"
        size="sm"
        class="zimmerkontingent-add ml-3"
        @click="addPlanzimmerkontingentRow"
      >
        <Plus :size="16" />
        Zimmertyp hinzufügen
      </b-button>
    </span>
    <hr class="w-100 ml-0" />
    <b-collapse id="collapse-zimmerkontingente-plan" :class="isOpen ? null : 'collapsed'" v-model="isOpen">
      <b-overlay :show="isLoading" class="zimmerkontingent-card">
        <div v-if="planZimmerkontingente.length > 0" class="zimmerkontingent-table">
          <div class="zimmerkontingent-head">
            <div>Zimmertyp</div>
            <div>Anzahl</div>
            <div>Teilt Kontingent mit</div>
            <div></div>
          </div>
          <div
            v-for="(zimmerkontingent, index) in planZimmerkontingente"
            :key="'planzimmerkontingent-' + index"
            class="zimmerkontingent-row"
          >
            <div>
              <AppSelect
                :value="zimmerkontingent.zimmertyp"
                :options="getAvailableZimmertypOptions(zimmerkontingent.zimmertyp)"
                label="Zimmertyp"
                placeholder="Zimmertyp wählen"
                class="zimmerkontingent-select"
                @input="onZimmertypInput(zimmerkontingent, $event)"
              >
                <template #selected="{ text }">
                  <span class="zimmerkontingent-single-label">
                    <span class="zimmerkontingent-single-label-text">{{ text }}</span>
                    <i
                      v-if="matchingSeasonZimmerkontingent(zimmerkontingent.zimmertyp) === null"
                      class="fas fa-asterisk text-warning icon-sm zimmerkontingent-saison-stern"
                      v-b-tooltip.hover
                      title="Kein passender Saison-Zimmertyp gefunden."
                    ></i>
                    <i
                      v-else-if="zimmertypDiffersSeason(zimmerkontingent)"
                      class="fas fa-asterisk text-warning icon-sm zimmerkontingent-saison-stern"
                      v-b-tooltip.hover
                      :title="seasonZimmerkontingentTooltip(zimmerkontingent)"
                    ></i>
                  </span>
                </template>
              </AppSelect>
            </div>
            <div>
              <AppSelect
                v-model="zimmerkontingent.anzahlEinheiten"
                :options="anzahlSelectOptions"
                label="Anzahl"
                placeholder="–"
                hide-search
                class="zimmerkontingent-anzahl"
                @change="onAnzahlEinheitenChange(zimmerkontingent)"
              />
            </div>
            <div>
              <AppSelect
                v-if="!zimmerkontingent.teiltkontingentMit"
                :value="null"
                :options="getAvailableZimmertypOptions(zimmerkontingent.teiltkontingentMit)"
                label="Kontingent teilen"
                placeholder="Kontingent teilen …"
                class="zimmerkontingent-select"
                @input="onTeiltKontingentInput(zimmerkontingent, $event)"
              />
              <div v-else class="zimmerkontingent-share">
                <span class="zimmerkontingent-share-code">{{ zimmerkontingent.teiltkontingentMit }}</span>
                <span class="zimmerkontingent-share-label">teilt</span>
                <AppSelect
                  v-model="zimmerkontingent.anzahlGeteiltesKontingent"
                  :options="getGeteiltesKontingentOptions(zimmerkontingent)"
                  label="Geteilte Anzahl"
                  hide-search
                  size="sm"
                  class="zimmerkontingent-share-count"
                />
                <button
                  type="button"
                  class="zimmerkontingent-share-x"
                  title="Teilung entfernen"
                  @click="clearTeiltKontingent(zimmerkontingent)"
                >
                  <X :size="14" />
                </button>
              </div>
            </div>
            <div class="zimmerkontingent-actions">
              <button
                type="button"
                class="zimmerkontingent-icon-btn"
                title="Zimmertyp entfernen"
                @click="removePlanzimmerkontingentRow(index)"
              >
                <Trash2 :size="16" />
              </button>
            </div>
          </div>
        </div>
        <div v-else class="zimmerkontingent-empty">Noch keine Zimmerkontingente angelegt.</div>
      </b-overlay>
    </b-collapse>

    <DeleteConfirmationModal
      id="modalZimmertypLoeschenReisetermin"
      title="Zimmertyp löschen?"
      entityBezeichnung="den Zimmertyp"
      :label="pendingRemoveLabel"
      @confirm="confirmRemovePlanzimmerkontingentRow"
    />
  </div>
</template>

<script>
import AppSelect from '@/components/common/app-select/app-select.vue';
import { Plus, Trash2, X } from 'lucide-vue';
import zimmertypenJson from '@/core/common/helpers/zimmertypen.json';
import apiService from '@/core/common/services/api.service';
import DeleteConfirmationModal from '@/components/common/delete-confirmation-modal.vue';

export default {
  name: 'SidebarTerminplanungCardPlanZimmerkontingente',
  components: {
    AppSelect,
    Plus,
    Trash2,
    X,
    DeleteConfirmationModal,
  },
  props: {
    // Serverstand der Reisetermin-Metadata & liefert die Baseline planZimmerkontingente für den Diff
    activeMetadata: { type: Object, default: null },
    // Zimmerkontingente der gültigen Saison, gegen die auf Abweichungen geprüft wird
    seasonPlanZimmerkontingente: { type: Array, default: () => [] },
    reisekuerzel: { type: String, default: null },
    startdatum: { type: String, default: null },
  },
  data() {
    return {
      zimmertypenJson,
      anzahlOptions: Array.from({ length: 16 }, (_, i) => i + 1),
      isLoading: false,
      isOpen: false,
      // kopie der Kontingente, die im UI bearbeitet wird
      planZimmerkontingente: [],
      // gemerkter Index/Label für die Löschbestätigung bereits gespeicherter Zimmertypen
      pendingRemoveIndex: null,
      pendingRemoveLabel: '',
    };
  },
  computed: {
    anzahlSelectOptions() {
      return this.anzahlOptions.map(anzahl => ({ value: anzahl, text: String(anzahl) }));
    },
    zimmertypOptions() {
      return Object.entries(this.zimmertypenJson)
        .map(([code, name]) => ({ value: code, text: `${code} – ${name}` }))
        .sort((a, b) => a.value.localeCompare(b.value));
    },
    // Sammelt alle bereits belegten Zimmertypen,
    // damit ein Typ in den Auswahllisten nicht doppelt vergeben werden kann
    usedZimmertypen() {
      const set = new Set();
      (this.planZimmerkontingente ?? []).forEach(zimmerkontingent => {
        if (zimmerkontingent.zimmertyp) set.add(zimmerkontingent.zimmertyp);
        if (zimmerkontingent.teiltkontingentMit) set.add(zimmerkontingent.teiltkontingentMit);
      });
      return set;
    },
    // Ermittelt neu anzulegende und geänderte Zimmerkontingente im Vergleich zum geladenen Stand
    // Löschungen erfolgen sofort serverseitig und sind daher nicht Teil dieses Diffs
    planZimmerkontingenteDiff() {
      const edited = this.planZimmerkontingente ?? [];
      const original = this.activeMetadata?.planZimmerkontingente ?? [];
      const toCreate = edited.filter(
        zimmerkontingent =>
          !zimmerkontingent.id &&
          zimmerkontingent.zimmertyp != null &&
          zimmerkontingent.anzahlEinheiten != null
      );
      const toUpdate = edited.filter(zimmerkontingent => {
        if (!zimmerkontingent.id) return false;
        const originalZimmerkontingent = original.find(item => item.id === zimmerkontingent.id);
        if (!originalZimmerkontingent) return false;
        return (
          originalZimmerkontingent.zimmertyp !== zimmerkontingent.zimmertyp ||
          +originalZimmerkontingent.anzahlEinheiten !== +zimmerkontingent.anzahlEinheiten ||
          (originalZimmerkontingent.anzahlGeteiltesKontingent ?? null) !==
            (zimmerkontingent.anzahlGeteiltesKontingent ?? null) ||
          (originalZimmerkontingent.teiltkontingentMit ?? null) !==
            (zimmerkontingent.teiltkontingentMit ?? null)
        );
      });
      return { toCreate, toUpdate };
    },
    planZimmerkontingenteChanged() {
      const { toCreate, toUpdate } = this.planZimmerkontingenteDiff;
      return toCreate.length > 0 || toUpdate.length > 0;
    },
    // Alle Zeilen müssen vollständig ausgefüllt sein
    planZimmerkontingenteValid() {
      return (this.planZimmerkontingente ?? []).every(
        zimmerkontingent =>
          zimmerkontingent.zimmertyp != null &&
          zimmerkontingent.anzahlEinheiten != null &&
          (!zimmerkontingent.teiltkontingentMit || zimmerkontingent.anzahlGeteiltesKontingent != null)
      );
    },
  },
  watch: {
    activeMetadata: {
      immediate: true,
      handler() {
        this.syncFromActive();
      },
    },
    planZimmerkontingente: {
      deep: true,
      handler() {
        this.emitChanged();
      },
    },
  },
  methods: {
    // Liefert die Zimmertyp-Optionen ohne bereits belegte Typen
    getAvailableZimmertypOptions(currentValue) {
      return this.zimmertypOptions.filter(
        option => !this.usedZimmertypen.has(option.value) || option.value === currentValue
      );
    },
    zimmertypOption(code) {
      if (!code) return null;
      return this.zimmertypOptions.find(option => option.value === code) ?? { value: code, text: code };
    },
    // Sucht den passenden Saison-Eintrag zum Zimmertyp
    matchingSeasonZimmerkontingent(zimmertyp) {
      if (!zimmertyp) return null;
      return (this.seasonPlanZimmerkontingente ?? []).find(item => item.zimmertyp === zimmertyp) ?? null;
    },
    // Prüft, ob die Zeile bei vorhandenem Saison-Zimmertyp von den Saisonwerten abweicht
    zimmertypDiffersSeason(zimmerkontingent) {
      const season = this.matchingSeasonZimmerkontingent(zimmerkontingent.zimmertyp);
      if (!season) return false;
      return (
        +season.anzahlEinheiten !== +zimmerkontingent.anzahlEinheiten ||
        (season.teiltkontingentMit ?? null) !== (zimmerkontingent.teiltkontingentMit ?? null) ||
        (season.anzahlGeteiltesKontingent ?? null) !== (zimmerkontingent.anzahlGeteiltesKontingent ?? null)
      );
    },
    // Baut den Tooltip-Text mit dem vollständigen Saisonwert der Zeile
    seasonZimmerkontingentTooltip(zimmerkontingent) {
      const season = this.matchingSeasonZimmerkontingent(zimmerkontingent.zimmertyp);
      if (!season) return '';
      let text = `Saisonwert: ${season.anzahlEinheiten}× ${season.zimmertyp}`;
      if (season.teiltkontingentMit) {
        text += `, teilt Kontingent mit ${season.anzahlGeteiltesKontingent} ${season.teiltkontingentMit}`;
      }
      return text;
    },
    onZimmertypInput(zimmerkontingent, zimmertyp) {
      zimmerkontingent.zimmertyp = zimmertyp ?? null;
    },
    onTeiltKontingentInput(zimmerkontingent, zimmertyp) {
      zimmerkontingent.teiltkontingentMit = zimmertyp ?? null;
      if (zimmerkontingent.teiltkontingentMit) {
        this.onTeiltKontingentSelected(zimmerkontingent);
      }
    },
    addPlanzimmerkontingentRow() {
      this.isOpen = true;
      this.planZimmerkontingente = [
        ...this.planZimmerkontingente,
        { zimmertyp: null, anzahlEinheiten: null, anzahlGeteiltesKontingent: null, teiltkontingentMit: null },
      ];
    },
    removePlanzimmerkontingentRow(index) {
      const zimmerkontingent = this.planZimmerkontingente[index];
      // bereits gespeicherte Zimmertypen erst nach Bestätigung entfernen
      if (zimmerkontingent?.id) {
        this.pendingRemoveIndex = index;
        this.pendingRemoveLabel =
          this.zimmertypOption(zimmerkontingent.zimmertyp)?.text ?? zimmerkontingent.zimmertyp;
        this.$bvModal.show('modalZimmertypLoeschenReisetermin');
        return;
      }
      this.removeRowAtIndex(index);
    },
    async confirmRemovePlanzimmerkontingentRow() {
      const index = this.pendingRemoveIndex;
      this.pendingRemoveIndex = null;
      this.pendingRemoveLabel = '';
      this.$bvModal.hide('modalZimmertypLoeschenReisetermin');
      if (index === null) return;
      // bereits gespeicherte Zimmertypen werden direkt serverseitig gelöscht
      await this.deletePlanzimmerkontingentRow(index);
    },
    // Löscht ein bereits gespeichertes Zimmerkontingent direkt am Server und aktualisiert den lokalen Stand
    async deletePlanzimmerkontingentRow(index) {
      const zimmerkontingent = this.planZimmerkontingente[index];
      if (!zimmerkontingent?.id) {
        this.removeRowAtIndex(index);
        return;
      }
      this.isLoading = true;
      try {
        await apiService.delete(
          `Reise/${this.reisekuerzel}/metadata/${this.startdatum}/planzimmerkontingent/reisetermin?planZimmerkontingentId=${zimmerkontingent.id}`
        );
        if (this.activeMetadata) {
          this.activeMetadata.planZimmerkontingente = (
            this.activeMetadata.planZimmerkontingente ?? []
          ).filter(item => item.id !== zimmerkontingent.id);
        }
        this.toast('Erfolgreich gelöscht.');
        this.syncFromActive();
      } catch {
        this.syncFromActive();
      } finally {
        this.isLoading = false;
      }
    },
    removeRowAtIndex(index) {
      this.planZimmerkontingente = this.planZimmerkontingente.filter((_, i) => i !== index);
    },
    // Die geteilte Anzahl kann maximal so groß sein wie die Anzahl des Zimmertyps
    getGeteiltesKontingentOptions(zimmerkontingent) {
      const max = zimmerkontingent.anzahlEinheiten ?? 0;
      return Array.from({ length: max }, (_, i) => ({ value: i + 1, text: String(i + 1) }));
    },
    // Beim Setzen eines geteilten Zimmertyps wird eine Standardanzahl vorbelegt
    onTeiltKontingentSelected(zimmerkontingent) {
      if (zimmerkontingent.anzahlGeteiltesKontingent == null) {
        zimmerkontingent.anzahlGeteiltesKontingent = 1;
      }
    },
    // Bei Verkleinerung der Anzahl wird die geteilte Anzahl auf das neue Maximum begrenzt
    onAnzahlEinheitenChange(zimmerkontingent) {
      if (
        zimmerkontingent.anzahlGeteiltesKontingent != null &&
        zimmerkontingent.anzahlGeteiltesKontingent > zimmerkontingent.anzahlEinheiten
      ) {
        zimmerkontingent.anzahlGeteiltesKontingent = zimmerkontingent.anzahlEinheiten;
      }
    },
    clearTeiltKontingent(zimmerkontingent) {
      zimmerkontingent.teiltkontingentMit = null;
      zimmerkontingent.anzahlGeteiltesKontingent = null;
    },
    // Speichert Anlage- und Änderungs-Zeilen
    async save() {
      const { toCreate, toUpdate } = this.planZimmerkontingenteDiff;
      const buildBody = zimmerkontingent => ({
        zimmertyp: zimmerkontingent.zimmertyp,
        anzahlEinheiten: zimmerkontingent.anzahlEinheiten,
        anzahlGeteiltesKontingent: zimmerkontingent.teiltkontingentMit
          ? zimmerkontingent.anzahlGeteiltesKontingent
          : null,
        teiltkontingentMit: zimmerkontingent.teiltkontingentMit ?? null,
      });
      const basePath = `Reise/${this.reisekuerzel}/metadata/${this.startdatum}/planzimmerkontingent/reisetermin`;
      await Promise.all(
        toCreate.map(zimmerkontingent => apiService.post(basePath, buildBody(zimmerkontingent)))
      );
      await Promise.all(
        toUpdate.map(zimmerkontingent =>
          apiService.put(
            `${basePath}?planZimmerkontingentId=${zimmerkontingent.id}`,
            buildBody(zimmerkontingent)
          )
        )
      );
    },
    syncFromActive() {
      this.planZimmerkontingente = (this.activeMetadata?.planZimmerkontingente ?? []).map(
        zimmerkontingent => ({ ...zimmerkontingent })
      );
    },
    // Meldet Änderungsstand und Gültigkeit an die Sidebar
    emitChanged() {
      this.$emit('changed', {
        hasChanges: this.planZimmerkontingenteChanged,
        isValid: this.planZimmerkontingenteValid,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/sass/components/_variables.bootstrap.scss';

.zimmerkontingent-card {
  padding: 20px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--surface);
}

.zimmerkontingent-table {
  margin-bottom: 4px;
}

.zimmerkontingent-head,
.zimmerkontingent-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 84px minmax(0, 1fr) 40px;
  column-gap: 12px;
  align-items: center;
}

.zimmerkontingent-head {
  padding: 8px 4px;
  border-bottom: 1px solid var(--border);
  font-size: $form-label-font-size;
  font-weight: $form-label-font-weight;
  color: $dark-75;
}

.zimmerkontingent-row {
  padding: 10px 8px;
  border-bottom: 1px solid #f1f1f4;
  transition: background-color 0.15s ease;
}

.zimmerkontingent-row:hover {
  background-color: #fafbfc;
}

.zimmerkontingent-select,
.zimmerkontingent-anzahl {
  --app-select-height: 33px;
}

/* Die Anzahl steht in einer 84px breiten Spalte */
.zimmerkontingent-anzahl {
  --app-select-padding-x: 6px;
  --app-select-font-size: 13px;
}

.zimmerkontingent-single-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
}

.zimmerkontingent-single-label-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.zimmerkontingent-saison-stern {
  flex: none;
  font-size: 0.8rem;
}

.zimmerkontingent-actions {
  display: flex;
  justify-content: flex-end;
}

.zimmerkontingent-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border: 0;
  border-radius: 6px;
  background-color: transparent;
  color: var(--text-subtle);
  cursor: pointer;
  transition: color 0.15s ease, background-color 0.15s ease;
}

.zimmerkontingent-icon-btn:hover {
  background-color: #fff0f2;
  color: var(--danger);
}

.zimmerkontingent-share {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  min-height: 33px;
  padding: 4px 6px 4px 12px;
  border: 1px solid rgba(54, 153, 255, 0.25);
  border-radius: 8px;
  background-color: rgba(54, 153, 255, 0.1);
}

.zimmerkontingent-share-code {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--primary);
}

.zimmerkontingent-share-label {
  font-size: 0.8rem;
  color: var(--text-muted);
}

.zimmerkontingent-share-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: 0;
  border-radius: 6px;
  background-color: transparent;
  color: var(--primary);
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.zimmerkontingent-share-x:hover {
  background-color: rgba(54, 153, 255, 0.18);
}

.zimmerkontingent-share-count {
  --app-select-width: auto;
  --app-select-height: 24px;
  --app-select-padding-x: 6px;
  --app-select-border-color: transparent;
  --app-select-radius: 6px;
  --app-select-background: var(--surface);
  --app-select-shadow: none;
  --app-select-color: var(--primary);
  --app-select-font-weight: 600;
  --app-select-font-size: 0.9rem;
}

.zimmerkontingent-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.zimmerkontingent-empty {
  padding: 24px;
  border: 1px dashed var(--border);
  border-radius: 8px;
  text-align: center;
  font-size: $form-label-font-size;
  font-weight: $form-label-font-weight;
  color: $dark-75;
}
</style>
